import React, { useState } from "react";
import { M } from "./tokens";

/* ══════════════════════════════════════════════════════════════
   ONE AD, AS THE MACHINE SEES IT
   The system underneath all six machines, shown through a single
   concrete artifact instead of an abstract explainer.
   ══════════════════════════════════════════════════════════════ */

const ACCENT = "#5FE3A1";

const LADDER = [
  ["Candidate", "Built, not yet read.", M.dim],
  ["Directional", "Moving the right way, under the volume floor.", M.warn],
  ["Validated", "One clean read at volume. Worth spending on.", ACCENT],
  ["Repeatable", "Held up in a second and third context.", "#7DD3FC"],
  ["Fatigued", "It won, then stopped. Retired with its history.", "#FF8FA3"],
  ["Insufficient data", "Not enough volume to say anything yet.", M.dim],
];

const AD_PIECES = [
  { id: "hook", label: "Hook", value: "HOOK-14", note: "The cost per night line, pulled from a review.", state: "Repeatable" },
  { id: "visual", label: "First frame", value: "FRAME-07", note: "Product in hand, kitchen light, no studio.", state: "Validated" },
  { id: "proof", label: "Proof", value: "PROOF-B", note: "4,812 reviews with one specific quote shown.", state: "Validated" },
  { id: "offer", label: "Offer tier", value: "TIER-MID", note: "The middle bundle, deliberately overweighted.", state: "Directional" },
  { id: "cta", label: "CTA", value: "CTA-02", note: "Verb plus outcome, no urgency language.", state: "Fatigued" },
];

const STATE_COLOR = {
  Candidate: M.dim,
  Directional: M.warn,
  Validated: ACCENT,
  Repeatable: "#7DD3FC",
  Fatigued: "#FF8FA3",
  "Insufficient data": M.dim,
};

export default function BuildingBlocks() {
  const [piece, setPiece] = useState("hook");
  const sel = AD_PIECES.find((p) => p.id === piece);

  return (
    <section data-testid="building-blocks" id="building-blocks" data-snap>
      <div className="mx-auto w-full max-w-[1280px] px-5 py-16 sm:px-8 sm:py-24">
        <div className="f-mono mb-4 text-[10px] uppercase tracking-[0.22em]" style={{ color: ACCENT }}>
          The system underneath all six machines
        </div>
        <h2 className="f-grotesk max-w-[24ch] text-[30px] font-bold leading-[1.05] tracking-[-0.025em] sm:text-[48px]">
          This is what one ad looks like to the machine.
        </h2>
        <p className="mt-5 max-w-[62ch] text-[15px] leading-[1.8]" style={{ color: M.muted }}>
          Not a picture. Five tracked pieces, each with its own record. Winners get reused across ads, emails and pages.
          Losers get retired. Click any piece.
        </p>

        <div className="mt-10 grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
          {/* the frame */}
          <div className="rounded-2xl border p-5" style={{ borderColor: M.border, background: M.surface }} data-testid="ad-anatomy">
            {AD_PIECES.map((p) => {
              const on = p.id === piece;
              return (
                <button
                  key={p.id}
                  onClick={() => setPiece(p.id)}
                  data-testid={`ad-piece-${p.id}`}
                  className="focus-ring mb-2 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors last:mb-0"
                  style={{
                    border: `1px ${on ? "solid" : "dashed"} ${on ? ACCENT : M.border}`,
                    background: on ? `${ACCENT}12` : "transparent",
                  }}
                  aria-pressed={on}
                >
                  <span className="f-mono w-[74px] shrink-0 text-[9px] uppercase tracking-[0.12em]" style={{ color: on ? ACCENT : M.dim }}>
                    {p.label}
                  </span>
                  <span className="f-mono min-w-0 flex-1 text-[11.5px]" style={{ color: M.ink }}>{p.value}</span>
                  <span
                    className="f-mono shrink-0 rounded px-2 py-[3px] text-[8px] uppercase tracking-[0.1em]"
                    style={{ color: STATE_COLOR[p.state], border: `1px solid ${STATE_COLOR[p.state]}55` }}
                  >
                    {p.state}
                  </span>
                </button>
              );
            })}
          </div>

          {/* the read on the selected piece */}
          <div className="rounded-2xl border p-6" style={{ borderColor: M.border, background: M.surface2 }} data-testid="ad-piece-detail">
            <div key={sel.id} style={{ animation: "sixIn 380ms cubic-bezier(0.16,1,0.3,1) both" }}>
              <div className="f-mono text-[9.5px] uppercase tracking-[0.16em]" style={{ color: STATE_COLOR[sel.state] }}>
                {sel.value} · {sel.state}
              </div>
              <div className="f-grotesk mt-2 text-[19px] font-semibold leading-snug">{sel.note}</div>
              <div className="mt-5 grid gap-2.5">
                {LADDER.map(([name, def, c]) => {
                  const on = name === sel.state;
                  return (
                    <div
                      key={name}
                      className="flex items-start gap-3 rounded-lg px-3 py-2.5"
                      style={{ background: on ? `${c}12` : "transparent", border: `1px solid ${on ? `${c}55` : "transparent"}` }}
                      data-testid={`ladder-${name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: c, opacity: on ? 1 : 0.45 }} />
                      <span className="min-w-0 flex-1">
                        <span className="f-mono block text-[9.5px] uppercase tracking-[0.12em]" style={{ color: on ? c : M.muted }}>{name}</span>
                        <span className="mt-0.5 block text-[12px] leading-snug" style={{ color: on ? M.ink : M.dim }}>{def}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* how each block earns autonomy */}
        <div className="mt-14" data-testid="trust-strip">
          <div className="f-mono mb-4 text-[10px] uppercase tracking-[0.22em]" style={{ color: ACCENT }}>
            Nothing starts autonomous · one workflow at a time
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[
              ["Weeks 0–2", "Done by hand, in your accounts, by a person"],
              ["Weeks 2–4", "Tuned until the output is right every time"],
              ["Weeks 4–6", "Becomes a standing job with its own permissions"],
              ["Weeks 6+", "Trusted per workflow, never one master switch"],
            ].map(([w, t]) => (
              <div key={w} className="rounded-xl border p-4" style={{ borderColor: M.border, background: M.surface }}>
                <div className="f-mono text-[9px] uppercase tracking-[0.16em]" style={{ color: ACCENT }}>{w}</div>
                <div className="mt-2 text-[13px] font-medium leading-snug">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
